/**
 * Passkey/WebAuthn Authentication Hooks
 *
 * TanStack Query v5 hooks for passkey-based authentication.
 * Secondary auth method for the unified AmalQ authentication system.
 *
 * Uses SimpleWebAuthn browser library for WebAuthn ceremonies.
 *
 * @module features/auth/hooks/usePasskey
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';

import {
  getPasskeyLoginOptions,
  verifyPasskeyLogin,
  getPasskeyRegistrationOptions,
  verifyPasskeyRegistration,
  listPasskeys,
  deletePasskey,
  renamePasskey,
  type PasskeyLoginVerifySuccessResponse,
  type Passkey,
} from '../services/userAuthApi';
import {
  authenticateWithPasskey,
  registerPasskey,
  parsePasskeyError,
} from '@lib/api/auth';
import { authKeys } from './queryKeys';

// ─────────────────────────────────────────────────────────────────────────────
// Hook Return Types
// ─────────────────────────────────────────────────────────────────────────────

export interface PasskeyLoginResult {
  user: PasskeyLoginVerifySuccessResponse['data']['user'];
  tokens: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  };
}

export interface PasskeyRegisterResult {
  credentialId: string;
  friendlyName: string;
  createdAt: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Passkey Login Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Authenticate with passkey
 *
 * Complete passkey login flow in one mutation:
 * 1. Get challenge options from server
 * 2. Trigger browser biometric prompt
 * 3. Verify assertion with server
 * 4. Store tokens on success
 *
 * @example
 * ```tsx
 * const { mutate: loginWithPasskey, isPending } = usePasskeyLogin({
 *   onSuccess: (result) => {
 *     console.log(`Welcome back ${result.user.name}`);
 *     // Navigate to dashboard
 *   },
 *   onError: (error) => {
 *     const passkeyError = parsePasskeyError(error);
 *     if (passkeyError.code === 'USER_CANCELLED') {
 *       // User cancelled biometric prompt
 *     }
 *   },
 * });
 *
 * const handlePasskeyLogin = (email: string) => {
 *   loginWithPasskey(email);
 * };
 * ```
 */
export function usePasskeyLogin(
  options?: Omit<
    UseMutationOptions<PasskeyLoginResult, Error, string>,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (email: string): Promise<PasskeyLoginResult> => {
      // Step 1: Get challenge options from server
      const challengeOptions = await getPasskeyLoginOptions(email);

      // Step 2: Trigger browser biometric prompt
      const assertion = await authenticateWithPasskey(challengeOptions);

      // Step 3: Verify assertion with server
      const response = await verifyPasskeyLogin(email, assertion);

      // Check for error response
      if (!response.success) {
        throw new Error(response.message || 'Passkey authentication failed');
      }

      // Type narrowing: at this point response is PasskeyLoginVerifySuccessResponse
      const successResponse = response as PasskeyLoginVerifySuccessResponse;

      // Return full data - let caller handle auth state via completeAuthentication
      // This follows single responsibility: hook handles API, caller handles auth state
      return {
        user: successResponse.data.user,
        tokens: {
          accessToken: successResponse.data.tokens.accessToken,
          refreshToken: successResponse.data.tokens.refreshToken,
          expiresIn: successResponse.data.tokens.expiresIn,
        },
      };
    },
    onSuccess: () => {
      // Invalidate all auth-related queries
      queryClient.invalidateQueries({
        queryKey: authKeys.all(),
      });
    },
    onError: (error) => {
      // Parse WebAuthn errors to user-friendly messages
      console.error('🔐 Passkey login failed:', parsePasskeyError(error));
    },
    ...options,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Passkey Registration Hook
// ─────────────────────────────────────────────────────────────────────────────

interface RegisterPasskeyParams {
  accessToken: string;
  friendlyName: string;
}

/**
 * Register a new passkey
 *
 * Adds a new passkey to the authenticated user's account.
 * Complete registration flow in one mutation:
 * 1. Get challenge options from server
 * 2. Trigger browser biometric prompt
 * 3. Verify credential with server
 *
 * @example
 * ```tsx
 * const { mutate: registerNewPasskey, isPending } = usePasskeyRegister({
 *   onSuccess: (result) => {
 *     toast.success(`Passkey "${result.friendlyName}" registered!`);
 *   },
 * });
 *
 * const handleRegister = (accessToken: string) => {
 *   registerNewPasskey({
 *     accessToken,
 *     friendlyName: 'My MacBook Pro',
 *   });
 * };
 * ```
 */
export function usePasskeyRegister(
  options?: Omit<
    UseMutationOptions<PasskeyRegisterResult, Error, RegisterPasskeyParams>,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      accessToken,
      friendlyName,
    }: RegisterPasskeyParams): Promise<PasskeyRegisterResult> => {
      // Step 1: Get challenge options from server
      const challengeOptions = await getPasskeyRegistrationOptions(accessToken);

      // Step 2: Trigger browser biometric prompt
      const credential = await registerPasskey(challengeOptions);

      // Step 3: Verify credential with server
      const response = await verifyPasskeyRegistration(
        accessToken,
        credential,
        friendlyName
      );

      return {
        credentialId: response.data.credentialId,
        friendlyName: response.data.friendlyName,
        createdAt: response.data.createdAt,
      };
    },
    onSuccess: () => {
      // Invalidate passkeys list to refetch with new passkey
      queryClient.invalidateQueries({
        queryKey: authKeys.passkeys.list(),
      });
    },
    onError: (error) => {
      // Parse WebAuthn errors to user-friendly messages
      console.error('🔐 Passkey registration failed:', parsePasskeyError(error));
    },
    ...options,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// List Passkeys Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch user's registered passkeys
 *
 * Returns list of all passkeys registered to the authenticated user's account.
 *
 * @example
 * ```tsx
 * const { data: passkeys, isLoading } = useListPasskeys(accessToken);
 *
 * return (
 *   <ul>
 *     {passkeys?.map((passkey) => (
 *       <li key={passkey.id}>
 *         {passkey.friendlyName} - Last used: {passkey.lastUsed || 'Never'}
 *       </li>
 *     ))}
 *   </ul>
 * );
 * ```
 */
export function useListPasskeys(
  accessToken: string | null,
  options?: Omit<
    UseQueryOptions<Passkey[], Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: authKeys.passkeys.list(),
    queryFn: () => {
      if (!accessToken) {
        throw new Error('Not authenticated');
      }
      return listPasskeys(accessToken);
    },
    enabled: !!accessToken,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Delete Passkey Hook
// ─────────────────────────────────────────────────────────────────────────────

interface DeletePasskeyParams {
  accessToken: string;
  passkeyId: string;
}

/**
 * Delete a registered passkey
 *
 * Removes a passkey from the user's account.
 * User must have at least one other auth method available.
 *
 * @example
 * ```tsx
 * const { mutate: removePasskey, isPending } = useDeletePasskey({
 *   onSuccess: () => {
 *     toast.success('Passkey removed');
 *   },
 * });
 *
 * const handleDelete = (passkeyId: string) => {
 *   removePasskey({ accessToken, passkeyId });
 * };
 * ```
 */
export function useDeletePasskey(
  options?: Omit<
    UseMutationOptions<void, Error, DeletePasskeyParams>,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ accessToken, passkeyId }: DeletePasskeyParams) => {
      await deletePasskey(accessToken, passkeyId);
    },
    onSuccess: () => {
      // Invalidate passkeys list to remove deleted passkey
      queryClient.invalidateQueries({
        queryKey: authKeys.passkeys.list(),
      });
    },
    ...options,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Rename Passkey Hook
// ─────────────────────────────────────────────────────────────────────────────

interface RenamePasskeyParams {
  accessToken: string;
  passkeyId: string;
  friendlyName: string;
}

/**
 * Rename a registered passkey
 *
 * Updates the friendly name of a passkey for easier identification.
 *
 * @example
 * ```tsx
 * const { mutate: updatePasskeyName, isPending } = useRenamePasskey({
 *   onSuccess: (updatedPasskey) => {
 *     toast.success(`Renamed to "${updatedPasskey.friendlyName}"`);
 *   },
 * });
 *
 * const handleRename = (passkeyId: string, newName: string) => {
 *   updatePasskeyName({ accessToken, passkeyId, friendlyName: newName });
 * };
 * ```
 */
export function useRenamePasskey(
  options?: Omit<
    UseMutationOptions<Passkey, Error, RenamePasskeyParams>,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      accessToken,
      passkeyId,
      friendlyName,
    }: RenamePasskeyParams) => {
      return renamePasskey(accessToken, passkeyId, friendlyName);
    },
    onSuccess: () => {
      // Invalidate passkeys list to reflect updated name
      queryClient.invalidateQueries({
        queryKey: authKeys.passkeys.list(),
      });
    },
    ...options,
  });
}

// Re-export parsePasskeyError for consumers
export { parsePasskeyError };
